import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Users, Trash2 } from "lucide-react";

export default function Clients() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });

  const load = () => api.get("/clients").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/clients", form);
      toast.success("Client added");
      setForm({ name: "", email: "", phone: "", address: "", notes: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    await api.delete(`/clients/${id}`);
    load();
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary mb-1">CRM</p>
          <h1 className="font-display text-4xl font-black tracking-tight">Clients</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-client-btn" className="h-11 rounded-sm btn-kinetic"><Plus className="w-4 h-4 mr-1" /> Add client</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle className="font-display text-2xl">New client</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div><Label>Name</Label><Input data-testid="client-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-sm" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input data-testid="client-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-sm" /></div>
                <div><Label>Phone</Label><Input data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 rounded-sm" placeholder="+1..." /></div>
              </div>
              <div><Label>Address</Label><Input data-testid="client-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-11 rounded-sm" /></div>
              <div><Label>Notes</Label><Textarea data-testid="client-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" /></div>
              <Button type="submit" data-testid="client-save-btn" className="w-full h-11 rounded-sm">Save client</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {list.length === 0 ? (
        <div className="bento-card p-12 text-center">
          <Users className="w-10 h-10 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">No clients yet. Add your first one to start building relationships.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {list.map((c) => (
            <div key={c.id} data-testid={`client-card-${c.id}`} className="bento-card p-4 flex justify-between items-start">
              <div>
                <p className="font-semibold text-lg">{c.name}</p>
                {c.email && <p className="text-xs text-muted-foreground">{c.email}</p>}
                {c.phone && <p className="text-xs text-muted-foreground">{c.phone}</p>}
                {c.address && <p className="text-xs text-muted-foreground mt-1">{c.address}</p>}
              </div>
              <button data-testid={`client-del-${c.id}`} onClick={() => del(c.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
